import { useEffect, useState } from "react";
import { Alert, Button, Checkbox, FormControlLabel, FormGroup, TextField, Typography } from "@mui/material";
import Modal from "../../../../shared/ui/common/Modal";
import { editPromotion, newPromotion, promotionPayload } from "../../lib/promotion-form";
import PromotionArtworkEditor from "./PromotionArtworkEditor";

export default function PromotionEditor({ open, kind, original, close, catalogue }) {
  const [value, setValue] = useState(newPromotion(kind)), [error, setError] = useState("");
  const [confirmDelete, setConfirmDelete] = useState(false), [confirmation, setConfirmation] = useState("");
  const [imageBusy, setImageBusy] = useState(false);
  useEffect(() => {
    if (!open) return;
    setValue(original ? editPromotion(kind, original) : newPromotion(kind)); setError("");
    setConfirmDelete(false); setConfirmation("");
  }, [open, kind, original]);
  const change = (name, next) => setValue((current) => ({ ...current, [name]: next }));
  const plans = kind === "plans";
  const activePlacements = catalogue.placements.filter((item) => item.enabled && (original?.isVip || !["search_banner", "category_index_top", "related_ads_top"].includes(item.code)));
  const unavailableCodes = catalogue.placementsLoading ? [] : (value.placementCodes || [])
    .filter((code) => !activePlacements.some((item) => item.code === code));
  async function submit(event) {
    event.preventDefault(); if (catalogue.saving || imageBusy || confirmDelete) return;
    setError("");
    try {
      if (plans && unavailableCodes.length) throw new Error("ابتدا ارتباط جایگاه‌های غیرفعال را حذف کنید.");
      if (await catalogue.save(original ? { ...original, version: value.version ?? original.version } : original, promotionPayload(kind, value))) close();
    }
    catch (reason) { setError(reason.message); }
  }
  async function remove() {
    if (!original || catalogue.saving || imageBusy || confirmation.trim() !== original.name.trim()) return;
    setError("");
    try { if (await catalogue.remove(original)) close(); } catch (reason) { setError(reason.message); }
  }
  return <Modal open={open} close={() => { if (!catalogue.saving && !imageBusy) close(); }}
    title={`${original ? "ویرایش" : "افزودن"} ${plans ? "پلن تبلیغاتی" : "جایگاه تبلیغاتی"}`}>
    <form onSubmit={submit} className="flex flex-col gap-4 p-2">
      {error && <Alert severity="error">{error}</Alert>}
      <PromotionArtworkEditor kind={kind} original={original} value={value} onBusy={setImageBusy} disabled={catalogue.saving}
        onSaved={(result) => { setValue((current) => ({ ...current, ...result })); catalogue.reload(); }} />
      {!plans && <TextField label="کد جایگاه" value={value.code || ""} disabled={Boolean(original) || catalogue.saving}
        onChange={(event) => change("code", event.target.value)} inputProps={{ maxLength: 64, dir: "ltr" }}
        helperText="کد، عنوان و توضیحات جایگاه از پیش تعریف شده‌اند و قابل تغییر نیستند." />}
      <TextField label={plans ? "نام پلن" : "عنوان جایگاه"} required disabled={!plans || catalogue.saving}
        value={(plans ? value.name : value.title) || ""} inputProps={{ maxLength: 100 }}
        onChange={(event) => change(plans ? "name" : "title", event.target.value)} />
      <TextField label="توضیحات" multiline minRows={2} disabled={!plans || catalogue.saving} inputProps={{ maxLength: 2000 }}
        value={value.description || ""} onChange={(event) => change("description", event.target.value)} />
      {!plans && <TextField label="ظرفیت هم‌زمان" type="number" required disabled={catalogue.saving}
        value={value.capacity ?? 0} inputProps={{ min: 0, max: 100, step: 1 }}
        helperText="صفر یعنی بسته‌بودن رزرو جدید؛ ظرفیت بنر سرچ بار حداکثر یک است."
        onChange={(event) => change("capacity", event.target.value)} />}
      {plans && <>
        <div className="grid sm:grid-cols-2 gap-3">
          <TextField label="قیمت (یورو)" disabled={catalogue.saving} value={value.price || ""}
            inputProps={{ inputMode: "decimal", dir: "ltr" }} onChange={(event) => change("price", event.target.value)} />
          <TextField label="قیمت (ریال)" disabled={catalogue.saving} value={value.rialPrice || ""}
            inputProps={{ inputMode: "numeric", dir: "ltr", maxLength: 10 }} onChange={(event) => change("rialPrice", event.target.value)}
            helperText="برای خرید با زرین‌پال؛ قیمت مستقل است و از یورو تبدیل نمی‌شود." />
          <TextField label="مدت اعتبار (روز)" required={value.enabled} type="number" disabled={catalogue.saving}
            value={value.durationDays || ""} inputProps={{ min: 1, max: 3650, step: 1 }}
            onChange={(event) => change("durationDays", event.target.value)} />
        </div>
        <Typography component="h3">جایگاه‌های هم‌زمان این پلن</Typography>
        {catalogue.placementsError && <Alert severity="error">{catalogue.placementsError}</Alert>}
        {catalogue.placementsLoading && <Typography>در حال دریافت جایگاه‌ها…</Typography>}
        {!catalogue.placementsLoading && !catalogue.placementsError && !activePlacements.length && <Typography>ابتدا یک جایگاه فعال تعریف کنید.</Typography>}
        {unavailableCodes.length > 0 && <Alert severity="warning" action={<Button disabled={catalogue.saving}
          onClick={() => change("placementCodes", value.placementCodes.filter((code) => !unavailableCodes.includes(code)))}>حذف ارتباط غیرفعال</Button>}>
          بعضی جایگاه‌های قبلی غیرفعال یا حذف شده‌اند؛ برای ذخیره ارتباط آن‌ها را حذف کنید.
        </Alert>}
        <FormGroup>{activePlacements.map((placement) => <FormControlLabel key={placement.code}
          label={placement.title}
          control={<Checkbox checked={Boolean(value.placementCodes?.includes(placement.code))} disabled={catalogue.saving}
            onChange={(event) => change("placementCodes", event.target.checked
              ? [...(value.placementCodes || []), placement.code] : value.placementCodes.filter((code) => code !== placement.code))} />} />)}</FormGroup>
      </>}
      <FormControlLabel label="فعال" control={<Checkbox checked={Boolean(value.enabled)} disabled={catalogue.saving}
        onChange={(event) => change("enabled", event.target.checked)} />} />
      {plans && original && catalogue.supportsArchive && !confirmDelete && <Button type="button" color="error" variant="outlined"
        disabled={catalogue.saving || imageBusy} onClick={() => setConfirmDelete(true)}>حذف پلن</Button>}
      {confirmDelete && original && <div className="flex flex-col gap-3">
        <Alert severity="warning">پلن از فهرست جاری حذف و غیرفعال می‌شود. بازیابی آن از فهرست حذف‌شده‌ها ممکن است.</Alert>
        <TextField label="نام پلن برای تأیید حذف" value={confirmation} disabled={catalogue.saving}
          helperText={`برای تأیید «${original.name}» را وارد کنید.`} inputProps={{ maxLength: 100 }}
          onChange={(event) => setConfirmation(event.target.value)} />
        <div className="flex gap-2">
          <Button type="button" color="error" variant="contained" onClick={remove}
            disabled={catalogue.saving || imageBusy || confirmation.trim() !== original.name.trim()}>تأیید حذف پلن</Button>
          <Button type="button" disabled={catalogue.saving} onClick={() => setConfirmDelete(false)}>انصراف از حذف</Button>
        </div>
      </div>}
      <div className="flex justify-end gap-3">
        <Button type="button" disabled={catalogue.saving || imageBusy} onClick={close}>انصراف</Button>
        <Button type="submit" variant="contained" disabled={imageBusy || catalogue.saving || confirmDelete || (plans && (catalogue.placementsLoading || Boolean(catalogue.placementsError)))}>
          {catalogue.saving ? "در حال ذخیره…" : "ثبت اطلاعات"}</Button>
      </div>
    </form>
  </Modal>;
}
